import { useEffect, useMemo, useRef, useState } from "react";
import { CirclePlus, SquarePen, Trash2, ShieldBan, Filter, Search, X, FileText, Code, Upload, Coins, Users, Pencil, Sparkles, Video } from "lucide-react";
import api from "../../../api/axios";
import { useNavigate, useSearchParams } from "react-router-dom";

import Table from "../../../components/common/Table";
import Pagination from "../../../components/common/Pagination";
import Modal from "../../../components/common/Modal";
import { listMatches, deleteMatch, aiTagMatch } from "../../../api/matches.api";
import useAuthStore from "../../../store/auth.store";
import useDialogDismiss from "../../../hooks/useDialogDismiss";

// Fallback used until /api/matches/matchtypes/ resolves (or if the
// fetch fails). The full list is loaded on mount so the dropdown
// reflects every active format — T10, T20, T20I, ODI, ODI50, Test,
// FC, HUNDRED, etc. — not just these three.
const FORMATS_FALLBACK = [
  { value: "", label: "All formats" },
  { value: "ODI", label: "ODI" },
  { value: "TEST", label: "Test" },
  { value: "T20", label: "T20" },
];

const ORDERINGS = [
  { value: "-match_date",    label: "Date — newest first" },
  { value: "match_date",     label: "Date — oldest first" },
  { value: "team1__name",    label: "Team A → Z" },
  { value: "-team1__name",   label: "Team Z → A" },
  { value: "match_type__code", label: "Format A → Z" },
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = (() => {
  const out = [{ value: "", label: "All years" }];
  for (let y = CURRENT_YEAR; y >= 2005; y--) out.push({ value: String(y), label: String(y) });
  return out;
})();

const STATE_TONE = {
  DRAFT:       "bg-bg-secondary text-text-secondary border-border-default",
  READY:       "bg-amber-500/15 text-amber-300 border-amber-400/30",
  IN_PROGRESS: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  COMPLETED:   "bg-blue-500/15 text-blue-300 border-blue-400/30",
};

// match_label arrives as e.g. "England vs New Zealand on 2024-12-06 (WTC 2024)"
// Strip the trailing competition suffix so the cell shows only the fixture.
const stripCompetitionTag = (label) =>
  (label || "").replace(/\s*\([^()]*\)\s*$/, "").trim();

// 2026-06-05 — mobile-only MobileList component retired per founder
// ask. Mobile + tablet now render the same DesktopMatchList as the
// laptop view. The shared Table component uses min-w-[640px] with
// overflow-x-auto, so on phones the row is horizontal-scrollable —
// same data shape, same icons, same filters. MobileList.jsx is left
// in the tree as dead code for one release in case we want to revert.
export default function MatchList() {
  return <DesktopMatchList />;
}

function DesktopMatchList() {
  const navigate = useNavigate();
  const isSuperuser = useAuthStore((s) => !!s.user?.is_superuser);
  const isOrg = useAuthStore(
    (s) => s.user?.account_type === "organization" || s.user?.account_type_v2 === "B2B",
  );

  // 2026-06-09 — Filters + page persist to URL search params so that
  // navigating into a match detail and back via the browser back
  // button restores the same view the operator left from. Without this
  // the List re-mounted with all filters wiped to "" and the operator
  // had to re-apply them every time. URL is also shareable now.
  const [searchParams, setSearchParams] = useSearchParams();
  const _initPage = (() => {
    const n = Number(searchParams.get("page") || 1);
    return Number.isFinite(n) && n > 0 ? n : 1;
  })();
  const [page, setPage] = useState(_initPage);
  const [matchType, setMatchType] = useState(searchParams.get("type") || "");
  const [year, setYear] = useState(searchParams.get("year") || "");
  const [competition, setCompetition] = useState(searchParams.get("comp") || "");
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [searchInput, setSearchInput] = useState(searchParams.get("q") || "");
  const [ordering, setOrdering] = useState(searchParams.get("order") || "match_date");

  // Sync committed filter / page / ordering state back to the URL with
  // replace: true so the back stack stays a clean list-of-pages, not a
  // list-of-keystrokes. Empty values are deleted from the URL so
  // /cricket/matches without filters has a clean URL.
  useEffect(() => {
    const next = new URLSearchParams();
    if (matchType)             next.set("type", matchType);
    if (year)                  next.set("year", year);
    if (competition)           next.set("comp", competition);
    if (search)                next.set("q", search);
    if (page !== 1)            next.set("page", String(page));
    if (ordering && ordering !== "match_date") next.set("order", ordering);
    setSearchParams(next, { replace: true });
    // setSearchParams is stable between renders — omitting from deps
    // (the eslint rule is satisfied with a comment) avoids a sync loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchType, year, competition, search, page, ordering]);

  // Format dropdown fed from /api/matches/matchtypes/ so every active
  // MatchType (T10, T20, T20I, ODI, ODI50, Test, FC, HUNDRED, …) is
  // selectable — not just the hardcoded 3 the filter used to ship with.
  const [formatOptions, setFormatOptions] = useState(FORMATS_FALLBACK);
  useEffect(() => {
    let alive = true;
    api.get("/api/matches/matchtypes/")
      .then((res) => {
        if (!alive) return;
        const rows = res.data?.results || res.data || [];
        if (!Array.isArray(rows) || rows.length === 0) return;
        setFormatOptions([
          { value: "", label: "All formats" },
          ...rows.map((r) => ({
            value: (r.code || r.name || "").toUpperCase(),
            label: r.name || r.code,
          })),
        ]);
      })
      .catch(() => { /* keep fallback */ });
    return () => { alive = false; };
  }, []);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Per-row AI Tag — confirms cost + fires the existing aiTagMatch endpoint.
  // Shows on rows where balls exist (IN_PROGRESS + COMPLETED). Backend
  // returns 402 if wallet is short → InsufficientCreditsModal handles it.
  const [aiTaggingId, setAiTaggingId] = useState(null);
  const [aiTagToast, setAiTagToast] = useState(null);  // {ok, msg}
  const runAiTagOnRow = async (rowId) => {
    if (aiTaggingId) return;
    if (!window.confirm("Run AI Tag on this match? Costs 15 Zencredits and fills missing analytics across every ball.")) return;
    setAiTaggingId(rowId);
    try {
      const res = await aiTagMatch(rowId, { fill_missing_only: true, max: 200 });
      const { tagged = 0, skipped = 0, error } = res.data || {};
      if (error) {
        setAiTagToast({ ok: false, msg: `AI Tag error: ${error}` });
      } else {
        setAiTagToast({ ok: true, msg: `AI tagged ${tagged} balls (${skipped} already complete).` });
      }
    } catch (e) {
      // 402 surfaces via the global wallet modal — don't double-alert.
      if (e?.response?.status === 402) return;
      setAiTagToast({
        ok: false,
        msg: e?.response?.data?.detail || e.message || "AI Tag failed",
      });
    } finally {
      setAiTaggingId(null);
      setTimeout(() => setAiTagToast(null), 4500);
    }
  };

  // Debounce the search input → committed value
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput.trim());
        setPage(1);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Competition dropdown: load competitions present in the user's
  // visible matches via the dedicated /competitions-in-scope/ endpoint.
  // It mirrors MatchViewSet's scope rules exactly (tenant + purchased
  // MATCH_BUNDLE + superuser SYSTEM bypass), so the dropdown always
  // matches what would actually filter the list below. The legacy
  // listCompetitions() only returned the user's own owner-scope
  // competitions and came back empty for anyone whose matches were
  // archive/SYSTEM-owned.
  const [competitions, setCompetitions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get("/api/matches/competitions-in-scope/")
      .then((res) => {
        if (cancelled) return;
        setCompetitions(res.data?.results || []);
      })
      .catch(() => { /* keep empty list */ });
    return () => { cancelled = true; };
  }, []);

  const competitionOptions = useMemo(() => {
    const opts = [{ value: "", label: "All competitions" }];
    [...competitions]
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .forEach((c) => opts.push({
        value: c.id,
        label: `${c.name}${c.season ? ` · ${c.season}` : ""}${c.match_count ? ` (${c.match_count})` : ""}`,
      }));
    return opts;
  }, [competitions]);

  const fetchPage = (p = page, mt = matchType, y = year, comp = competition, s = search, ord = ordering) => {
    setLoading(true);
    // When a competition is selected, default to match_number ascending
    // so playoff fixtures (#29-32) sit naturally below the round-robin
    // (#1-28) instead of being shuffled by date/created_at. Operator's
    // own Sort selection still wins — only override when ordering is
    // the page's default (match_date / -match_date).
    const effectiveOrdering = (
      comp && (!ord || ord === "match_date" || ord === "-match_date")
    ) ? "match_number" : ord;
    listMatches(p, {
      match_type: mt || undefined,
      year: y || undefined,
      competition: comp || undefined,
      search: s || undefined,
      ordering: effectiveOrdering || undefined,
    })
      .then((res) => {
        // 2026-06-07 — annotate each row with its absolute match number
        // (across pages) so the table can render a stable `#` column.
        // Page size hardcoded to 20 to match Pagination.jsx + the DRF
        // DEFAULT_PAGINATION_CLASS PAGE_SIZE. Persisting the number on
        // the row (rather than computing at render time) lets the
        // Table's client-side sort keep the right number against the
        // right match instead of always showing 1,2,3 in current order.
        const PAGE_SIZE = 20;
        const enriched = {
          ...res.data,
          results: (res.data?.results || []).map((r, i) => ({
            ...r,
            _match_number: (p - 1) * PAGE_SIZE + i + 1,
          })),
        };
        setData(enriched);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, ordering]);

  // Quick-launch keyboard shortcut. Press "g" (or Enter when nothing
  // is focused on this page) to jump straight to the most recent
  // in-progress match's coding screen — saves operators with several
  // ongoing matches a click + a UI scan. Disabled while typing in an
  // input / select / textarea.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = (e.key || "").toLowerCase();
      if (k !== "g" && e.key !== "Enter") return;
      const inProgress = (data?.results || []).find((r) => r.state === "IN_PROGRESS");
      if (inProgress) {
        navigate(`/ball/scoring/${inProgress.id}`);
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [data, navigate]);

  const onApply = (next) => {
    const mt = next.matchType ?? matchType;
    const y = next.year ?? year;
    const comp = next.competition ?? competition;
    setMatchType(mt); setYear(y); setCompetition(comp);
    setPage(1);
    fetchPage(1, mt, y, comp, search);
  };
  const onClear = () => {
    onApply({ matchType: "", year: "", competition: "" });
    setSearchInput(""); setSearch("");
  };

  const refresh = () => fetchPage();

  const total = data?.count ?? 0;
  const filterActive = matchType !== "" || year !== "" || competition !== "" || search !== "";

  return (
    <div className="p-3 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl font-bold">Matches</h2>
          <p className="text-sm text-text-secondary mt-0.5">
            {loading
              ? "Loading…"
              : <>Showing <span className="text-text-primary font-medium">{(data?.results || []).length}</span> of <span className="text-text-primary font-medium">{total.toLocaleString()}</span> matches{filterActive ? " (filtered)" : ""}</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Bulk import is a B2B / admin tool — Cricsheet / vendor JSON loads
              one match at a time. B2C scorers never have files in that shape. */}
          {(isOrg || isSuperuser) && (
            <ImportMatchButton onImported={() => window.location.reload()} />
          )}
          <a
            href="/cricket/matches/add"
            className="bg-button-primary hover:brightness-110 px-4 py-2 rounded-md flex gap-2 items-center w-fit text-sm font-semibold transition-all"
          >
            <CirclePlus className="w-4" />
            Add Match
          </a>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-3 max-w-xl">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by team name or CI match ID…"
          className="w-full bg-bg-primary border border-border-default rounded-md py-2 pl-9 pr-9 text-sm text-text-primary placeholder-text-secondary/60 focus:outline-none focus:border-button-primary transition-colors"
        />
        {searchInput && (
          <button
            onClick={() => setSearchInput("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors p-1"
            title="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Filter bar — grid so the 4 selects + Clear don't overflow on
          phones (was: flex with min-w-[140/260]px that exploded at 414).
          Phone: stack. sm: 2-col. lg: 4-col with Competition spanning 2. */}
      <div className="bg-bg-primary border border-border-default rounded-lg p-4 mb-6">
        <div className="flex items-center gap-2 text-sm text-text-secondary mb-3">
          <Filter className="w-4 h-4" />
          <span className="uppercase tracking-wider text-xs">Filters</span>
          {filterActive && (
            <button
              onClick={onClear}
              className="ml-auto text-xs px-3 py-1.5 rounded-md bg-bg-secondary border border-border-default hover:bg-button-primary/15 hover:border-button-primary/40 transition-colors"
            >
              Clear
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="flex flex-col">
            <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1">Format</label>
            <select
              value={matchType}
              onChange={(e) => onApply({ matchType: e.target.value })}
              className="w-full bg-bg-main border border-border-default rounded-md py-1.5 px-3 text-sm focus:outline-none focus:border-button-primary"
            >
              {formatOptions.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1">Year</label>
            <select
              value={year}
              onChange={(e) => onApply({ year: e.target.value })}
              className="w-full bg-bg-main border border-border-default rounded-md py-1.5 px-3 text-sm focus:outline-none focus:border-button-primary"
            >
              {YEARS.map((y) => <option key={y.value} value={y.value}>{y.label}</option>)}
            </select>
          </div>

          <div className="flex flex-col sm:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1">Competition / Series</label>
            <select
              value={competition}
              onChange={(e) => onApply({ competition: e.target.value })}
              className="w-full bg-bg-main border border-border-default rounded-md py-1.5 px-3 text-sm focus:outline-none focus:border-button-primary"
            >
              {competitionOptions.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1">Sort</label>
            <select
              value={ordering}
              onChange={(e) => { setOrdering(e.target.value); setPage(1); }}
              className="w-full bg-bg-main border border-border-default rounded-md py-1.5 px-3 text-sm focus:outline-none focus:border-button-primary"
            >
              {ORDERINGS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {aiTagToast && (
        <div className={`mb-3 rounded-md border px-3 py-2 text-sm ${
          aiTagToast.ok
            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
            : "border-rose-500/40 bg-rose-500/10 text-rose-200"
        }`}>
          {aiTagToast.ok ? "✓" : "⚠"} {aiTagToast.msg}
        </div>
      )}

      {loading && (
        <div className="rounded-lg border border-border-default bg-bg-primary p-10 text-center text-text-secondary text-sm">
          Loading…
        </div>
      )}

      {!loading && filterActive && (data?.results || []).length === 0 && (
        <div className="rounded-2xl border border-border-default bg-bg-primary p-8 text-center">
          <h3 className="text-base font-bold text-text-primary mb-2">No matches match your filters</h3>
          <p className="text-sm text-text-secondary mb-4">
            Try a different search term, year, format, or competition — or clear all filters to see your full list.
          </p>
          <button
            onClick={() => { setMatchType(""); setYear(""); setCompetition(""); setSearch(""); }}
            className="text-xs uppercase tracking-wider px-3 py-1.5 rounded-md bg-bg-secondary border border-border-default hover:border-button-primary text-text-primary"
          >
            Clear filters
          </button>
        </div>
      )}

      {!loading && !filterActive && (data?.results || []).length === 0 && (
        <div className="rounded-2xl border border-border-default bg-bg-primary p-8 sm:p-10 text-center">
          <div className="max-w-2xl mx-auto">
            <h3 className="text-xl font-bold text-text-primary mb-2">Score your first match</h3>
            <p className="text-sm text-text-secondary mb-6">
              You'll need teams + players in place before a match can be created. Three quick steps:
            </p>
            <div className="grid sm:grid-cols-3 gap-3 text-left">
              <a
                href="/cricket/teams/add"
                className="block rounded-lg border border-border-default bg-bg-secondary/40 p-4 hover:border-button-primary/40 hover:bg-button-primary/10 transition-colors"
              >
                <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-1">Step 1</div>
                <div className="font-semibold text-text-primary mb-1">Add teams</div>
                <div className="text-xs text-text-secondary">
                  At least two — your batting side and the opposition.
                </div>
              </a>
              <a
                href="/cricket/players/add"
                className="block rounded-lg border border-border-default bg-bg-secondary/40 p-4 hover:border-button-primary/40 hover:bg-button-primary/10 transition-colors"
              >
                <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-1">Step 2</div>
                <div className="font-semibold text-text-primary mb-1">Add players</div>
                <div className="text-xs text-text-secondary">
                  11 per team for a full XI. Add to a team while you create them.
                </div>
              </a>
              <a
                href="/cricket/matches/add"
                className="block rounded-lg border border-button-primary/40 bg-button-primary/15 p-4 hover:bg-button-primary/25 transition-colors"
              >
                <div className="text-[10px] uppercase tracking-wider text-button-primary mb-1">Step 3</div>
                <div className="font-semibold text-text-primary mb-1">Create your match</div>
                <div className="text-xs text-text-secondary">
                  Pick teams + ground + format. Toss and playing XI come next.
                </div>
              </a>
            </div>
            <p className="mt-6 text-[11px] text-text-secondary">
              Already added some teams? Hit <span className="font-mono">Add Match</span> at the top right.
            </p>
          </div>
        </div>
      )}

      {!loading && ((data?.results || []).length > 0 || filterActive) && (
        <div>
          <Table
            columns={[
              { key: "_match_number", label: "#", sortable: false, render: (r) => (
                // Prefer the source match_number (e.g. "5" for Match 5 of
                // TNPL 2024) when populated; fall back to the absolute
                // row index across pages so the column is never blank.
                <span className="font-mono text-text-secondary text-xs">
                  {r.match_number ?? r._match_number}
                </span>
              )},
              { key: "match",  label: "Match", render: (r) => (
                <div>
                  <div className="font-medium">{r.team1_name && r.team2_name ? `${r.team1_name} vs ${r.team2_name}` : stripCompetitionTag(r.match_label)}</div>
                  <div className="text-[11px] text-text-secondary">{r.match_date || "—"}</div>
                </div>
              )},
              { key: "match_type_name", label: "Format" },
              { key: "match_mode",      label: "Mode" },
              { key: "state",           label: "State", render: (r) => (
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border w-fit ${STATE_TONE[r.state] || STATE_TONE.DRAFT}`}>
                      {r.state || "—"}
                    </span>
                    {/* "Ready" tick — match prep complete, just hit CODE */}
                    {r.state === "READY" && (
                      <span
                        className="text-[10px] text-emerald-300"
                        title="Toss + Playing XI complete — ready for ball-by-ball"
                      >
                        ✓ ready
                      </span>
                    )}
                  </div>
                  {r.result_type && (
                    <span className={`text-[9px] uppercase tracking-wider rounded-full px-1.5 py-0.5 border w-fit ${
                      r.result_type === "DECISIVE"  ? "border-emerald-400/30 text-emerald-300 bg-emerald-500/10" :
                      r.result_type === "TIE"       ? "border-amber-400/30 text-amber-300 bg-amber-500/10" :
                      r.result_type === "NO_RESULT" ? "border-text-secondary/30 text-text-secondary bg-bg-secondary/30" :
                      r.result_type === "ABANDONED" ? "border-red-400/30 text-red-300 bg-red-500/10" :
                                                      "border-purple-400/30 text-purple-300 bg-purple-500/10"
                    }`}>
                      {r.result_type.replaceAll("_", " ")}
                    </span>
                  )}
                </div>
              )},
            ]}
            data={data}
            rowActions={(row) => (
              // 2026-06-06 — was flex+overflow-x-auto+max-w-[70vw] which
              // clipped labels mid-word ("ecard", "ODE", "DEL TE") because
              // the strip was scrollable but only ~70vw wide. Founder
              // flagged this on the mobile match list. Wrap on all sizes
              // so every action is readable; horizontal scroll on the
              // table outer still handles narrow rows.
              <div className="flex flex-wrap items-center justify-end gap-2">
                {row.state === "COMPLETED" ? (
                  <>
                    <button
                      data-testid={`match-list-scorecard-${row.id}`}
                      onClick={() => navigate(`/cricket/matches/${row.id}/scorecard`)}
                      className="flex gap-1.5 bg-button-primary hover:brightness-110 px-3 py-1 items-center rounded text-xs font-semibold text-white transition-all"
                      title="View scorecard"
                    >
                      <FileText className="w-3.5" />
                      View Scorecard
                    </button>
                    <button
                      onClick={() => navigate(`/cricket/matches/${row.id}/edit-balls`)}
                      className="flex gap-1.5 bg-bg-secondary hover:bg-blue-500/15 hover:border-blue-400/40 border border-border-default px-2 py-1 items-center rounded text-xs text-text-primary transition-all"
                      title="Edit balls in this match"
                    >
                      <Pencil className="w-3.5" />
                      Edit balls
                    </button>
                    <button
                      onClick={() => runAiTagOnRow(row.id)}
                      disabled={aiTaggingId === row.id}
                      className="flex gap-1.5 bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/40 px-2 py-1 items-center rounded text-xs text-blue-300 transition-all disabled:opacity-50"
                      title="Run AI Tag on this match — 15 Zencredits"
                    >
                      <Sparkles className="w-3.5" />
                      {aiTaggingId === row.id ? "Tagging…" : "AI Tag · 15 ZC"}
                    </button>
                    <button
                      onClick={() => navigate(`/admin/matches/${row.id}/videos`)}
                      className="flex gap-1.5 bg-bg-secondary hover:bg-purple-500/15 hover:border-purple-400/40 border border-border-default px-2 py-1 items-center rounded text-xs text-text-primary transition-all"
                      title="Upload + watch ball videos for this match"
                    >
                      <Video className="w-3.5" />
                      Videos
                    </button>
                  </>
                ) : (
                  <>
                    {/* Prep shortcuts for matches that aren't yet live.
                        Once IN_PROGRESS, scoring is the only relevant action. */}
                    {row.state !== "IN_PROGRESS" && (
                      <>
                        <button
                          data-testid={`match-list-toss-${row.id}`}
                          onClick={() => navigate(`/cricket/matches/${row.id}/toss`)}
                          className="flex gap-1.5 bg-bg-secondary hover:bg-amber-500/15 hover:border-amber-400/40 border border-border-default px-2 py-1 items-center rounded text-xs text-text-primary transition-all"
                          title="Set toss"
                        >
                          <Coins className="w-3.5" />
                          Toss
                        </button>
                        <button
                          onClick={() => navigate(`/cricket/matches/${row.id}/squad`)}
                          className="flex gap-1.5 bg-bg-secondary hover:bg-purple-500/15 hover:border-purple-400/40 border border-border-default px-2 py-1 items-center rounded text-xs text-text-primary transition-all"
                          title="Pick the match squad (15–20 players per team)"
                        >
                          <Users className="w-3.5" />
                          Squad
                        </button>
                        <button
                          data-testid={`match-list-xi-${row.id}`}
                          onClick={() => navigate(`/cricket/matches/${row.id}/playing-xi`)}
                          className="flex gap-1.5 bg-bg-secondary hover:bg-blue-500/15 hover:border-blue-400/40 border border-border-default px-2 py-1 items-center rounded text-xs text-text-primary transition-all"
                          title="Pick playing XI from the squad"
                        >
                          <Users className="w-3.5" />
                          Playing XI
                        </button>
                      </>
                    )}
                    <button
                      data-testid={`match-list-code-${row.id}`}
                      onClick={() => navigate(`/cricket/matches/${row.id}/confirm-scoring`)}
                      className="flex gap-1.5 bg-emerald-600 hover:brightness-110 px-3 py-1 items-center rounded text-xs font-semibold text-white transition-all"
                      title={row.state === "IN_PROGRESS" ? "Resume scoring" : "Confirm and start scoring"}
                    >
                      <Code className="w-3.5" />
                      CODE
                    </button>
                    {!row.is_locked && (
                      <button
                        onClick={() => setDeleteId(row.id)}
                        className="flex gap-1.5 bg-rose-600/90 hover:bg-rose-600 px-3 py-1 items-center rounded text-xs font-semibold text-white transition-all"
                        title="Delete this match (permanent)"
                      >
                        <Trash2 className="w-3.5" />
                        DELETE
                      </button>
                    )}
                    {row.state === "IN_PROGRESS" && (
                      <button
                        onClick={() => runAiTagOnRow(row.id)}
                        disabled={aiTaggingId === row.id}
                        className="flex gap-1.5 bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/40 px-2 py-1 items-center rounded text-xs text-blue-300 transition-all disabled:opacity-50"
                        title="Run AI Tag on this match — 15 Zencredits"
                      >
                        <Sparkles className="w-3.5" />
                        {aiTaggingId === row.id ? "Tagging…" : "AI Tag · 15 ZC"}
                      </button>
                    )}
                    {row.state === "IN_PROGRESS" && (
                      <button
                        onClick={() => navigate(`/cricket/matches/${row.id}/edit-balls`)}
                        className="flex gap-1.5 bg-bg-secondary hover:bg-blue-500/15 hover:border-blue-400/40 border border-border-default px-2 py-1 items-center rounded text-xs text-text-primary transition-all"
                        title="Edit any past ball in this match"
                      >
                        <Pencil className="w-3.5" />
                        Edit balls
                      </button>
                    )}
                  </>
                )}

                {!row.is_locked ? (
                  <a href={`/cricket/matches/${row.id}/edit`}>
                    <button disabled={row.is_locked} title="Edit">
                      <SquarePen className="w-4 text-text-secondary hover:text-button-primary transition-colors" />
                    </button>
                  </a>
                ) : (
                  <ShieldBan className="w-4 text-text-secondary" title="Locked" />
                )}
              </div>
            )}
          />

          <Pagination count={total} page={page} onPage={setPage} />
        </div>
      )}

      <Modal
        open={!!deleteId}
        title="Delete this match?"
        body={(
          <>
            <div>This will permanently remove the match and all its scoring data — toss, playing XIs, innings, balls, and any per-ball videos. This action cannot be undone.</div>
            {deleteError && (
              <div className="mt-3 text-left bg-rose-500/10 border border-rose-500/30 rounded p-2 text-rose-300 text-xs whitespace-pre-wrap break-words">
                {deleteError}
              </div>
            )}
          </>
        )}
        confirmLabel={deleting ? "Deleting…" : "Delete match"}
        confirmDisabled={deleting}
        confirmDestructive
        onConfirm={async () => {
          if (deleting) return;
          setDeleting(true);
          setDeleteError(null);
          try {
            await deleteMatch(deleteId);
            setDeleteId(null);
            setDeleting(false);
            refresh();
          } catch (e) {
            setDeleting(false);
            const detail = e?.response?.data?.detail
              || (e?.response?.data ? JSON.stringify(e.response.data) : null)
              || e?.message
              || "Delete failed";
            setDeleteError(`${detail}${e?.response?.status ? ` (HTTP ${e.response.status})` : ""}`);
          }
        }}
        onCancel={() => { setDeleteId(null); setDeleteError(null); setDeleting(false); }}
      />
    </div>
  );
}

function ImportMatchButton({ onImported }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const safeClose = busy ? () => {} : () => setOpen(false);
  useDialogDismiss({ open, onClose: safeClose, ref: panelRef });

  const submit = async () => {
    if (!file) return;
    setBusy(true); setError(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/api/matches/import/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      onImported?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => { setOpen(true); setFile(null); setResult(null); setError(""); }}
        className="bg-bg-secondary hover:bg-bg-main border border-border-default px-3 py-2 rounded-md flex gap-2 items-center text-sm transition-all"
      >
        <Upload className="w-4" />
        Bulk import
      </button>
      {open && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4"
          role="presentation"
          onClick={() => !busy && setOpen(false)}
        >
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Bulk import match"
            className="bg-bg-primary rounded-xl sm:rounded-2xl max-w-sm sm:max-w-lg w-full max-h-[90vh] overflow-y-auto border border-bg-secondary shadow-2xl p-4 sm:p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold">Bulk-import match</h3>
              <button onClick={() => !busy && setOpen(false)} className="text-text-secondary hover:text-text-primary">✕</button>
            </div>
            <p className="text-xs text-text-secondary mb-3">
              Upload a <span className="text-text-primary font-medium">Cricsheet-format JSON</span> file
              for one match. Teams and players auto-create if they don&apos;t exist yet.
              {" "}<a className="text-button-primary underline" href="https://cricsheet.org/format/json/" target="_blank" rel="noreferrer">Format reference</a>
            </p>

            {!result && (
              <>
                <input
                  type="file"
                  accept=".json,application/json"
                  onChange={(e) => { setFile(e.target.files?.[0] || null); setError(""); }}
                  disabled={busy}
                  className="w-full text-sm text-text-primary file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-bg-secondary file:text-text-primary hover:file:bg-bg-main mb-3"
                />
                {error && <div className="text-xs text-red-400 mb-2">{error}</div>}
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setOpen(false)} disabled={busy}
                    className="px-3 py-1.5 rounded-md bg-bg-secondary text-text-secondary hover:bg-bg-main">
                    Cancel
                  </button>
                  <button
                    onClick={submit}
                    disabled={!file || busy}
                    className="px-3 py-1.5 rounded-md bg-button-primary text-white disabled:opacity-50"
                  >
                    {busy ? "Importing…" : "Import"}
                  </button>
                </div>
              </>
            )}

            {result && (
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-emerald-400">
                  <span className="text-base">✓</span>
                  <span className="font-semibold">Imported successfully</span>
                </div>
                <div className="bg-bg-secondary/30 rounded p-3 text-xs space-y-1">
                  <div><span className="text-text-secondary">Innings:</span> <span className="font-mono text-text-primary">{result.innings_count}</span></div>
                  <div><span className="text-text-secondary">Balls:</span> <span className="font-mono text-text-primary">{result.ball_count}</span></div>
                  <div><span className="text-text-secondary">Teams created:</span> <span className="font-mono text-text-primary">{result.teams_created}</span></div>
                  <div><span className="text-text-secondary">Players created:</span> <span className="font-mono text-text-primary">{result.players_created?.length ?? 0}</span></div>
                  {result.warnings?.length > 0 && (
                    <details className="mt-1">
                      <summary className="text-amber-400 cursor-pointer">{result.warnings.length} warning(s)</summary>
                      <ul className="mt-1 pl-3 text-text-secondary">
                        {result.warnings.slice(0, 10).map((w, i) => <li key={i}>· {w}</li>)}
                      </ul>
                    </details>
                  )}
                </div>
                <div className="flex gap-2 justify-end pt-2">
                  <button onClick={() => setOpen(false)}
                    className="px-3 py-1.5 rounded-md bg-bg-secondary text-text-secondary hover:bg-bg-main">
                    Close
                  </button>
                  <button
                    onClick={() => navigate(`/cricket/matches/${result.match_id}/scorecard`)}
                    className="px-3 py-1.5 rounded-md bg-button-primary text-white"
                  >
                    Open scorecard
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
